import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Divider,
    Chip,
    Avatar,
    alpha,
    useTheme,
    CircularProgress
} from '@mui/material';
import {
    CreditCard as CardIcon,
    History as HistoryIcon,
    Star as PremiumIcon,
    CheckCircle as ActiveIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import confetti from 'canvas-confetti';
import { billingApi } from '../services/api';

const Billing = () => {
    const theme = useTheme();
    const location = useLocation();
    const [, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCelebration, setShowCelebration] = useState(false);

    const queryParams = new URLSearchParams(location.search);
    const isSuccess = queryParams.get('success') === 'true';
    const isCanceled = queryParams.get('canceled') === 'true';

    useEffect(() => {
        if (isSuccess && !showCelebration) {
            triggerConfetti();
        }
    }, [isSuccess, showCelebration]);

    const triggerConfetti = () => {
        setShowCelebration(true);
        const duration = 7 * 1000;
        const animationEnd = Date.now() + duration;

        // Emojis for the celebration
        const scalar = 2;
        const star = confetti.shapeFromText({ text: '⭐', scalar });
        const balloon = confetti.shapeFromText({ text: '🎈', scalar });
        const party = confetti.shapeFromText({ text: '🎉', scalar });
        const pinata = confetti.shapeFromText({ text: '🪅', scalar });
        const sparkling = confetti.shapeFromText({ text: '✨', scalar });

        const interval = setInterval(function () {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            // 1. Triple Blast from bottom center (shooting up and falling down)
            confetti({
                particleCount: 3,
                angle: 90,
                spread: 45,
                origin: { x: 0.5, y: 1 },
                startVelocity: 45,
                gravity: 1.2,
                shapes: [star, balloon, party, pinata, sparkling],
                ticks: 300
            });

            // 2. Rain from the top (falling straight down)
            confetti({
                particleCount: 2,
                angle: 270, // Directing downwards
                spread: 180,
                origin: { x: Math.random(), y: -0.1 },
                startVelocity: 5,
                gravity: 0.5,
                shapes: [star, sparkling],
                ticks: 400
            });

            // 3. Side cannons for variety
            if (timeLeft % 1000 < 100) {
                confetti({
                    particleCount: 40,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0, y: 0.6 },
                    colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
                });
                confetti({
                    particleCount: 40,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1, y: 0.6 },
                    colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
                });
            }
        }, 150);
    };

    const handleManagePortal = async () => {
        try {
            const { url } = await billingApi.createPortalSession();
            if (url) {
                window.location.href = url;
            }
        } catch (err) {
            console.error('Error opening customer portal:', err);
            alert('Failed to open billing management. Please try again later.');
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                // Fetch user data from Firestore for plan details
                if (db) {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        setUserData(userDoc.data());
                    }
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const currentPlan = userData?.plan || 'free';
    const isPremium = currentPlan !== 'free';

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', py: 4, px: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Billing & Subscription</Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>Manage your subscription and view your payment history.</Typography>

            {isSuccess && (
                <Box sx={{
                    mb: 5,
                    p: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    animation: 'fadeInUp 0.6s ease-out',
                    '@keyframes fadeInUp': {
                        from: { opacity: 0, transform: 'translateY(20px)' },
                        to: { opacity: 1, transform: 'translateY(0)' }
                    }
                }}>
                    <Box sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 3,
                        boxShadow: `0 4px 15px ${alpha(theme.palette.success.main, 0.4)}`
                    }}>
                        <ActiveIcon sx={{ color: '#fff', fontSize: 35 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'success.dark', mb: 0.5 }}>
                            Welcome to the Club! 🚀
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Your payment was successful and your account has been upgraded. Enjoy the full power of AI for your social media growth.
                        </Typography>
                    </Box>
                </Box>
            )}

            {isCanceled && (
                <Box sx={{
                    mb: 5,
                    p: 3,
                    borderRadius: 4,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <Typography sx={{ mr: 2, fontSize: 24 }}>ℹ️</Typography>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'info.dark' }}>Checkout Canceled</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Your payment was not completed. No charges were made.
                        </Typography>
                    </Box>
                </Box>
            )}

            <Grid container spacing={3}>
                <Grid xs={12} md={7}>
                    <Card sx={{
                        borderRadius: 4,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        overflow: 'visible'
                    }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                <Box>
                                    <Typography variant="overline" color="primary" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>Current Plan</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, textTransform: 'capitalize' }}>
                                        {currentPlan.replace('_', ' ')}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={userData?.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
                                    color={userData?.status === 'active' ? 'success' : 'default'}
                                    variant="soft"
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                />
                            </Box>

                            <Box sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.03),
                                p: 3,
                                borderRadius: 3,
                                border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                                mb: 4
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PremiumIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Plan Benefits</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {isPremium
                                        ? "You have full access to all AI features, multi-platform posting, and the advanced engagement moderation queue."
                                        : "You are currently on the Free tier. Upgrade to Pro to unlock AI-powered content analysis and multi-platform scheduling."
                                    }
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    component={Link}
                                    to="/app/pricing"
                                    startIcon={<PremiumIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.2,
                                        px: 3,
                                        boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                                    }}
                                >
                                    {isPremium ? "Change Plan" : "Upgrade to Pro"}
                                </Button>
                                {isPremium && (
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={handleManagePortal}
                                        sx={{ borderRadius: 2, px: 3, borderColor: alpha(theme.palette.divider, 0.2) }}
                                    >
                                        Cancel Subscription
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid xs={12} md={5}>
                    <Card sx={{
                        height: '100%',
                        borderRadius: 4,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main, mr: 2 }}>
                                    <CardIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>Payment Method</Typography>
                            </Box>

                            {userData?.stripe_customer_id ? (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Registered with Stripe Customer ID:
                                    </Typography>
                                    <Typography variant="mono" sx={{
                                        fontSize: '0.75rem',
                                        bgcolor: alpha(theme.palette.divider, 0.1),
                                        p: 1,
                                        borderRadius: 1,
                                        display: 'block',
                                        wordBreak: 'break-all'
                                    }}>
                                        {userData.stripe_customer_id}
                                    </Typography>
                                    <Button
                                        fullWidth
                                        size="small"
                                        onClick={handleManagePortal}
                                        sx={{ mt: 3, justifyContent: 'space-between' }}
                                        endIcon={<ChevronRightIcon />}
                                    >
                                        Manage in Customer Portal
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ py: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No payment method on file.
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <HistoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Invoices</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Your invoice history will appear here once your first payment is processed.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Billing;
